import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { db } from '../firebase/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaCheckCircle, FaUser, FaFileAlt } from 'react-icons/fa';

const Anesthesia = ({ handleSuccessDataSubmission, patientInfo }) => {
  const [userEmail, setUserEmail] = useState('');
  const [outcomeDescription, setOutcomeDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const handleSuccessSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!outcomeDescription.trim()) {
      toast.error('Please describe the outcome of the surgery.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        toast.error('No user logged in');
        setIsSubmitting(false);
        return;
      }

      let practice = '';
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          practice = userDocSnap.data().practice;
        } else {
          toast.error('User data not found');
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Error fetching user data');
        setIsSubmitting(false);
        return;
      }

      const submissionData = {
        userEmail,
        patientId: patientInfo?.patientID || '',
        outcomeDescription: outcomeDescription.trim(),
        practice,
        procedureId: patientInfo?.procedureId || null,
        procedureType: patientInfo?.procedureType || null,
        physicianId: patientInfo?.physicianId || null,
        physicianName: patientInfo?.physicianName || null
      };

      await handleSuccessDataSubmission(submissionData);

      // Reset form
      setOutcomeDescription('');
      
    } catch (error) {
      console.error('Error submitting success form:', error);
      toast.error('Failed to record success');
    } finally {
      setIsSubmitting(false);
    }
  }, [outcomeDescription, userEmail, handleSuccessDataSubmission, patientInfo]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl">
        {/* Header Card */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-green-100">
            <FaCheckCircle className="size-8 text-green-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Anesthesia Procedure Success
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Record a successful anesthesia procedure completion
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <div className="px-6 py-8 sm:px-10 sm:py-10">
            <form onSubmit={handleSuccessSubmit} className="space-y-8">
              {/* Patient ID Display (read-only) */}
              {patientInfo?.patientID && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <FaUser className="size-4 text-gray-400" />
                    Patient ID
                  </label>
                  <div className="mt-2">
                    <div className="block w-full rounded-md bg-gray-50 px-3 py-2.5 text-base text-gray-700 border border-gray-300">
                      {patientInfo.patientID}
                    </div>
                  </div>
                </div>
              )}

              {/* Procedure Info Display (if procedure is linked) */}
              {patientInfo?.procedureId && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <FaCheckCircle className="size-4 text-gray-400" />
                    Linked Procedure
                  </label>
                  <div className="mt-2">
                    <div className="block w-full rounded-md bg-gray-50 px-3 py-2.5 text-base text-gray-700 border border-gray-300">
                      {patientInfo.procedureId} {patientInfo.procedureType ? `- ${patientInfo.procedureType}` : ''}
                    </div>
                  </div>
                </div>
              )}

              {/* Outcome Description Field */}
              <div>
                <label htmlFor="outcomeDescription" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FaFileAlt className="size-4 text-gray-400" />
                  Outcome Description
                  <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <textarea
                    id="outcomeDescription"
                    name="outcomeDescription"
                    rows={6}
                    value={outcomeDescription}
                    onChange={(e) => setOutcomeDescription(e.target.value)}
                    required
                    placeholder="Describe the outcome of the surgery..."
                    className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Provide details about the successful outcome of the procedure.
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-center gap-x-4 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-x-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                      <span>Submit</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Anesthesia;
